import { PiDownloadSimpleBold } from 'react-icons/pi';
import { Link } from 'react-router-dom';
import V2Modal from '@v2/components/ui/V2Modal';
import { Badge, Button } from '@v2/design-system';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import { FileTypeIcon } from './FileGridCard';
import type { PloomesFileItem } from './types';

type Props = {
    file: PloomesFileItem | null;
    onClose: () => void;
};

export default function FilePreviewModal({ file, onClose }: Props) {
    if (!file) return null;

    return (
        <V2Modal
            open={!!file}
            onClose={onClose}
            title={file.display_name}
            size="2xl"
            footer={
                <>
                    {file.access_url && (
                        <a href={file.access_url} target="_blank" rel="noopener noreferrer">
                            <Button type="button" variant="outline" as="span">
                                <PiDownloadSimpleBold className="me-1.5 size-4" />
                                Abrir arquivo
                            </Button>
                        </a>
                    )}
                    <Button type="button" onClick={onClose}>Fechar</Button>
                </>
            }
        >
            <div className="space-y-5">
                {/* Preview da imagem ou placeholder */}
                {file.is_image && file.access_url ? (
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                        <img
                            src={file.access_url}
                            alt={file.display_name}
                            className="mx-auto max-h-80 w-full object-contain"
                        />
                    </div>
                ) : (
                    <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
                        <FileTypeIcon file={file} large />
                    </div>
                )}

                {/* Metadados */}
                <dl className="grid gap-3 sm:grid-cols-2">
                    <Meta label="Categoria"   value={file.category_label ?? '—'} />
                    <Meta label="Tamanho"     value={file.readable_size ?? '—'} />
                    <Meta
                        label="Validação"
                        value={
                            file.is_validated
                                ? <Badge color="success" variant="flat" rounded="pill">Validado</Badge>
                                : <Badge color="warning" variant="flat" rounded="pill">Pendente</Badge>
                        }
                    />
                    <Meta label="Comprovante" value={file.is_receipt ? 'Sim' : 'Não'} />
                    <Meta
                        label="Criado em"
                        value={
                            file.ploomes_created_at
                                ? new Date(file.ploomes_created_at).toLocaleString('pt-BR')
                                : '—'
                        }
                    />
                    {file.file_name && <Meta label="Arquivo original" value={file.file_name} />}
                    {file.deal && (
                        <Meta
                            label="Negócio Ploomes"
                            value={
                                <Link
                                    to={ploomesDealPath(file.deal)}
                                    className="text-primary hover:underline"
                                    onClick={onClose}
                                >
                                    {file.deal.title ?? file.deal.deal_number ?? `#${file.deal.id}`}
                                </Link>
                            }
                        />
                    )}
                </dl>

                {file.notes && (
                    <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        {file.notes}
                    </div>
                )}
            </div>
        </V2Modal>
    );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {label}
            </dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{value}</dd>
        </div>
    );
}
